import { useState, useEffect } from "react";

export type VisitorIndustry = 
  | "restaurant" 
  | "hotel" 
  | "healthcare" 
  | "warehouse" 
  | "education" 
  | "retail"
  | "unknown";

export interface VisitorProfile {
  industry: VisitorIndustry;
  visitCount: number;
  lastVisit: string;
  interests: string[];
  referrer: string | null;
  isReturning: boolean;
}

const STORAGE_KEY = "neil_ai_visitor_profile";

// Detect industry from referrer, UTM params, or browsing behavior
const detectIndustry = (): VisitorIndustry => {
  const referrer = document.referrer.toLowerCase();
  const pathname = window.location.pathname.toLowerCase();
  const searchParams = new URLSearchParams(window.location.search);
  const utmCampaign = searchParams.get("utm_campaign")?.toLowerCase() || "";
  const utmContent = searchParams.get("utm_content")?.toLowerCase() || "";
  
  // Check referrer for industry signals
  if (referrer.includes("restaurant") || referrer.includes("food") || referrer.includes("dining")) {
    return "restaurant";
  }
  if (referrer.includes("hotel") || referrer.includes("hospitality") || referrer.includes("lodging")) {
    return "hotel";
  }
  if (referrer.includes("health") || referrer.includes("medical") || referrer.includes("hospital")) {
    return "healthcare";
  }
  if (referrer.includes("warehouse") || referrer.includes("logistics") || referrer.includes("supply")) {
    return "warehouse";
  }
  if (referrer.includes("school") || referrer.includes("education") || referrer.includes("university")) {
    return "education";
  }
  
  // Check UTM parameters
  const utmText = `${utmCampaign} ${utmContent}`;
  if (utmText.includes("restaurant") || utmText.includes("food")) return "restaurant";
  if (utmText.includes("hotel") || utmText.includes("hospitality")) return "hotel";
  if (utmText.includes("health") || utmText.includes("medical")) return "healthcare";
  if (utmText.includes("warehouse") || utmText.includes("logistics")) return "warehouse";
  if (utmText.includes("school") || utmText.includes("education")) return "education";
  if (utmText.includes("retail") || utmText.includes("store")) return "retail";
  
  // Check current page
  if (pathname.includes("/restaurants")) return "restaurant";
  if (pathname.includes("/hotels")) return "hotel";
  if (pathname.includes("/healthcare")) return "healthcare";
  if (pathname.includes("/warehouses")) return "warehouse";
  if (pathname.includes("/education")) return "education";
  
  return "unknown";
};

// Get personalized content based on visitor profile
export const getPersonalizedContent = (profile: VisitorProfile) => {
  const industryContent: Record<VisitorIndustry, {
    headline: string;
    subheadline: string;
    cta: string;
    featuredRobots: string[];
    benefits: string[];
  }> = {
    restaurant: {
      headline: "Automate Your Restaurant with AI Robots",
      subheadline: "BellaBot and KettyBot Pro deliver food faster, wow customers, and reduce labor costs by 40%",
      cta: "See Restaurant Solutions",
      featuredRobots: ["BellaBot Pro", "KettyBot Pro", "HolaBot"],
      benefits: ["Faster table turnover", "Reduced wait staff costs", "Memorable dining experience"]
    },
    hotel: {
      headline: "Transform Hotel Operations with Robotics",
      subheadline: "From room service to cleaning, our robots work 24/7 so your staff can focus on guest satisfaction",
      cta: "Explore Hotel Solutions",
      featuredRobots: ["SwiftBot", "CC1 Pro", "FlashBot Max"],
      benefits: ["24/7 room service delivery", "Consistent cleaning standards", "Enhanced guest experience"]
    },
    healthcare: {
      headline: "Safe, Reliable Healthcare Robotics",
      subheadline: "PUDU robots help reduce infection risk while improving delivery efficiency in medical facilities",
      cta: "View Healthcare Solutions",
      featuredRobots: ["SwiftBot", "PuduBot 2", "T300 Series"],
      benefits: ["Reduced infection transmission", "Faster medication delivery", "Staff safety improvement"]
    },
    warehouse: {
      headline: "Industrial-Grade Automation for Warehouses",
      subheadline: "Heavy-duty robots that handle up to 600kg payloads and integrate with your existing systems",
      cta: "Discover Warehouse Solutions",
      featuredRobots: ["PUDU T600", "FlashBot Arm", "PUDU D9"],
      benefits: ["600kg payload capacity", "WMS integration", "24/7 operation capability"]
    },
    education: {
      headline: "Bring Innovation to Your Campus",
      subheadline: "Robots that serve, clean, and inspire students while reducing operational costs",
      cta: "See Education Solutions",
      featuredRobots: ["BellaBot", "CC1", "KettyBot Pro"],
      benefits: ["Cafeteria automation", "Campus cleaning", "STEM inspiration for students"]
    },
    retail: {
      headline: "Retail Robots That Drive Sales",
      subheadline: "Interactive robots that guide customers, promote products, and create memorable shopping experiences",
      cta: "Explore Retail Solutions",
      featuredRobots: ["KettyBot Pro", "BellaBot Pro", "PuduBot 2"],
      benefits: ["Interactive product promotion", "Customer engagement", "Store wayfinding"]
    },
    unknown: {
      headline: "Go Fully Autonomous with Neil AI",
      subheadline: "Voice Automation, CRM Integration & AI Solutions that Scale Your Business While You Sleep",
      cta: "Book a Free Demo",
      featuredRobots: ["BellaBot Pro", "CC1 Pro", "SwiftBot"],
      benefits: ["Save up to 60% on labor", "24/7 autonomous operation", "Boost customer satisfaction"]
    }
  };
  
  return industryContent[profile.industry];
};

export const useVisitorPersonalization = () => {
  const [profile, setProfile] = useState<VisitorProfile>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as VisitorProfile;
        return {
          ...parsed,
          visitCount: parsed.visitCount + 1,
          lastVisit: new Date().toISOString(),
          isReturning: true
        };
      }
    } catch {
      // Ignore localStorage errors
    }
    
    return {
      industry: "unknown",
      visitCount: 1,
      lastVisit: new Date().toISOString(),
      interests: [],
      referrer: document.referrer || null,
      isReturning: false
    };
  });

  // Detect and update industry on mount
  useEffect(() => {
    const detectedIndustry = detectIndustry();
    
    setProfile(prev => {
      // Keep existing industry if already set and user is returning
      const industry = prev.isReturning && prev.industry !== "unknown" 
        ? prev.industry 
        : detectedIndustry;
      
      return { ...prev, industry };
    });
  }, []);

  // Track interests based on page visits
  useEffect(() => {
    const pathname = window.location.pathname;
    
    setProfile(prev => {
      const interests = new Set(prev.interests);
      
      // Add interests based on pages visited
      if (pathname.includes("/robots/")) {
        const robotName = pathname.split("/robots/")[1]?.replace(/-/g, " ");
        if (robotName) interests.add(robotName);
      }
      if (pathname.includes("/restaurants")) interests.add("restaurant");
      if (pathname.includes("/hotels")) interests.add("hotel");
      if (pathname.includes("/healthcare")) interests.add("healthcare");
      if (pathname.includes("/warehouses")) interests.add("warehouse");
      if (pathname.includes("/ai-automation")) interests.add("ai-automation");
      
      return { ...prev, interests: Array.from(interests) };
    });
  }, []);

  // Persist profile to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // Ignore localStorage errors
    }
  }, [profile]);

  const updateIndustry = (industry: VisitorIndustry) => {
    setProfile(prev => ({ ...prev, industry }));
  };

  const personalizedContent = getPersonalizedContent(profile);

  return {
    profile,
    personalizedContent,
    updateIndustry,
    isReturning: profile.isReturning,
    visitCount: profile.visitCount
  };
};
